import { takeEvery, call, getContext, select } from 'redux-saga/effects';
import { RootAction } from 'store/state';
import { MUTATE, SET_MUTATION_RESPONSE } from 'store/state/mutationsResponse/types';
import { MutationType } from 'store/sagas/apiService/types';
import { SetMutationResponseAction } from 'store/state/mutationsResponse/actions';
import { ListingStatusType, PocType, IBulletin, ICommercialBulletin } from 'utils/entities';
import { ReasonType } from 'components/update-listing-status-popup/helpers';
import { poiEventPayload } from 'store/sagas/analytics/property';
import { userProfileSelector } from 'store/state/domainData/selectors';
import { allPoisByIdsHeapSelector } from 'store/state/selectors/poisHeap';

let privateDeletedPoi: IBulletin | ICommercialBulletin = null;

const editListingStatusPattern = (action: RootAction) => action.type === MUTATE && (
  action.mutationType === MutationType.EditListingStatus || action.mutationType === MutationType.EditCommercialListingStatus
);

export const mapReasonToAnalytics: Record<string, string> = {
  [ReasonType.Sold]: 'property_sold',
  [ReasonType.Rented]: 'property_rented',
  [ReasonType.StoppedAdvertising]: 'marketing_stopped',
  [ReasonType.Other]: 'other',
};

interface IAnalyticsReason {
  reason: string;
  reason_text: string;
}

const parseReason = (reason: string): IAnalyticsReason => {
  if (mapReasonToAnalytics[reason]) return { reason: mapReasonToAnalytics[reason], reason_text: '' };
  if (reason === 'other') {
    return { reason: 'other', reason_text: '' };
  }
  if (reason && reason.length) {
    return { reason: 'other', reason_text: reason.substring(0, 500) };
  }

  return { reason: '', reason_text: '' };
};

function* updateListingStatusWatcher(action: RootAction) {
  let variables = null;
  if (action.type === MUTATE && (
    action.mutationType === MutationType.EditListingStatus || action.mutationType === MutationType.EditCommercialListingStatus)) {
    variables = action.meta.variables;
  }

  if (!variables || !variables.editListingStatus || !variables.source) return;

  const { id, status, hideReason  } = variables.editListingStatus;
  const { source } = variables;

  const currentUser = yield select(userProfileSelector);
  const poisHeap = yield select(allPoisByIdsHeapSelector);
  const poi = poisHeap[id];
  if (!poi || poi.poc && poi.poc.type === PocType.Agent) return;

  let eventName = '';
  let eventAction = null;
  if (status === ListingStatusType.Displayed) {
    eventName = 'bulletin_manage_freeze_form_submit';
    eventAction = 'unfreeze';
  }
  else if (status === ListingStatusType.Frozen) {
    eventName = 'bulletin_manage_freeze_form_submit';
    eventAction = 'freeze';
  }
  else if (status === ListingStatusType.Deleted) {
    eventName = 'bulletin_manage_delete_form_submit';
    privateDeletedPoi = poi;
  }

  if (!eventName) return;

  const poiPayload = poiEventPayload({
    poi,
    estimatedPrice: null,
    isLeadingAgent: false,
    currentUser,
  });

  const { sendEvent } = yield getContext('analytics');
  yield call(
    sendEvent, eventName, 'bulletin_manage', {
      ...poiPayload,
      event: {
        ...(parseReason(hideReason)),
        ...(eventAction ? { action: eventAction } : null),
        source,
      },
    });
}


const editListingStatusSuccessPattern = (action: RootAction) => action.type === SET_MUTATION_RESPONSE && (
  action.mutationType === MutationType.EditListingStatus
  || action.mutationType === MutationType.EditCommercialListingStatus
);

function* updateListingStatusSuccessWatcher(action: SetMutationResponseAction<MutationType.EditListingStatus | MutationType.EditCommercialListingStatus>) {
  const { data } = action.meta.response;
  let variables = null;

  switch (action.mutationType) {
    case MutationType.EditCommercialListingStatus:
      if ('editCommercialListingStatus' in data && data.editCommercialListingStatus.commercialListing) {
        variables = action.meta.variables;
      }
      break;
    case MutationType.EditListingStatus:
      if ('editListingStatus' in data && data.editListingStatus.listing) {
        variables = action.meta.variables;
      }
      break;
  }

  if (!variables || !variables.source) return;

  const { id, status, hideReason  } = variables.editListingStatus;

  const currentUser = yield select(userProfileSelector);
  const poisHeap = yield select(allPoisByIdsHeapSelector);
  const poi = poisHeap[id];

  if ((poi && poi.poc && poi.poc.type === PocType.Agent) ||
    (!privateDeletedPoi && status === ListingStatusType.Deleted) ||
    (!poi && status !== ListingStatusType.Deleted)
  ) return;

  let eventName = '';
  let eventAction = null;
  if (status === ListingStatusType.Displayed) {
    eventName = 'bulletin_manage_freeze_form_submit_success';
    eventAction = 'unfreeze';
  }
  else if (status === ListingStatusType.Frozen) {
    eventName = 'bulletin_manage_freeze_form_submit_success';
    eventAction = 'freeze';
  }
  else if (status === ListingStatusType.Deleted) {
    eventName = 'bulletin_manage_delete_form_submit_success';
  }

  const eventPoi = status === ListingStatusType.Deleted ? privateDeletedPoi : poi;
  const poiPayload = poiEventPayload({
    poi: eventPoi,
    estimatedPrice: null,
    isLeadingAgent: false,
    currentUser,
  });

  const { sendEvent } = yield getContext('analytics');
  yield call(
    sendEvent, eventName, 'bulletin_manage', {
      ...poiPayload,
      event: {
        ...(parseReason(hideReason)),
        ...(eventAction ? { action: eventAction } : null),
        source: variables.source,
      },
    });

  privateDeletedPoi = null;
}

export function* updateListingStatusWorker() {
  yield takeEvery(editListingStatusPattern, updateListingStatusWatcher);
  yield takeEvery(editListingStatusSuccessPattern, updateListingStatusSuccessWatcher);
}
